import React, { PureComponent, Fragment } from 'react'
import ReactDOMServer from 'react-dom/server'
import i18n from 'i18next'
import AnimateHeight from 'react-animate-height'
import { reaction } from 'mobx'
import { observer } from 'mobx-react'
import * as R from 'ramda'

import DebugInfo from '../DebugInfo'
import ReportPageTitle from '../ReportPageTitle'
import { getQuestionTitle, getOptionTitle } from '../../../../../utils/QuestionHelper'
import Panel from '../../../../../UniformComponents/Panel'
import PanelHeader from '../../../../../UniformComponents/PanelHeader'
import QuestionEdit from '../QuestionEdit'
import GroupedRows from './GroupedRows'

@observer
class TextListBox extends PureComponent {
    constructor(props) {
        super(props)
        const { question, reportStore, pageQuestionId } = this.props
        const optionReduce = question.optionSet.options.reduce((obj, o) => {
            obj.isOpen[o.id.toUpperCase()] = false
            obj.responses[o.id.toUpperCase()] = {
                numberOfResponses: 0,
                responses: [],
            }
            return obj
        }, {
            isOpen: {},
            responses: {},
        })
        this.state = {
            isLoading: false,
            isOpen: optionReduce.isOpen,
            responses: optionReduce.responses,
        }

        reaction(
            () => props.reportStore.reportDataFetched,
            () => {
                this.getReportData()
            },
        )

        reportStore.verbatim[pageQuestionId] = this.renderPDF
        reportStore.questionSorting[props.dataKey] = this.getQuestionSorting
    }

    componentDidMount() {
        this.getReportData()
    }

    componentWillUnmount() {
        const { reportStore, pageQuestionId } = this.props
        Reflect.deleteProperty(reportStore.verbatim, pageQuestionId)
    }

    mapResponses = (response) => (
        R.sortWith([
            R.descend(R.prop('numberOfResponses')),
            R.ascend(R.prop('label')),
        ], response.labels.map((label, index) => ({
            label,
            numberOfResponses: R.pathOr(0, ['numberOfResponses', index], response),
            surveyDataIds: R.pathOr([], ['surveyDataIds', index], response),
        })))
    )

    getQuestionSorting = () => {
        const { responses } = this.state
        const { question, reportStore, store } = this.props
        const { survey } = store
        const options = []
        const numberOfResponse = []
        const labels = []

        question.optionSet.options.map((option) => {
            const optionResponses = responses[option.id.toUpperCase()]?.responses ?? []
            const optionLabels = Array(optionResponses.length).fill('')
            const optionLabel = getOptionTitle({
                langs: option.langs,
                locale: reportStore.locale,
                defaultLocale: survey.metadata.defaultLocale,
                initialLocale: question.metadata.initialLocale,
            })
            optionLabels[0] = optionLabel
            options.push(...optionLabels)

            optionResponses.forEach(({ label, numberOfResponses }) => {
                numberOfResponse.push(numberOfResponses)
                labels.push(`${optionLabel} - (${label})`)
            })
        })
        return [
            labels.map((_, i) => i),
            'bar',
            numberOfResponse,
            labels,
            options,
        ]
    }

    getReportData = async () => {
        const { question, reportStore } = this.props
        const { isOpen } = this.state
        const { questionTypeCode } = question
        const { appStore } = reportStore
        try {
            const request = JSON.stringify({
                questionId: question.id,
                questionType: questionTypeCode,
                questionOptionId: null,
                filter: {
                    state: reportStore.getReportStates(),
                    surveyDataIds: reportStore.getSurveyDataIdFilter(reportStore.filters.openEnded, 'include'),
                    excludeSurveyDataIds: reportStore.getSurveyDataIdFilter(reportStore.filters.openEnded, 'exclude'),
                    ...R.omit(['openEnded'], reportStore.filters),
                },
            })

            const response = await appStore.http.post(`/report/v1/${reportStore.surveyId}/textquestion`, request)

            const responses = Object.entries(response).reduce((map, [optionId, optionData]) => (
                Object.assign(map, {
                    [optionId.toUpperCase()]: {
                        numberOfResponses: optionData.numberOfResponses.reduce((a, b) => a + b),
                        responses: this.mapResponses(optionData),
                    },
                })
            ), {})

            reportStore.openEnded.set(question.id, responses)

            this.setState({
                responses,
                isLoading: false,
                isOpen,
            })
        } catch (e) {
            console.error(`Error getting getReportData -> ${e}`)
        }
    }

    addVerbatim = (e) => {
        const { reportStore } = this.props
        const { responses } = this.state

        const index = parseInt(e.currentTarget.attributes.getNamedItem('data-index').value, 10)
        reportStore.useVerbatimFilter(responses.surveyDataIds[index])
    }

    renderPDF = () => (ReactDOMServer.renderToStaticMarkup(this.resultRows(true)))

    renderGroupedRows = () => {
        const { store, question, reportStore } = this.props
        return (
            <GroupedRows
                question={question}
                renderRows={this.resultRows}
                cols={{}}
                reportStore={reportStore}
                store={store}
            />
        )
    }

    renderFilter = (label, optionId, surveyDataIds) => {
        const { reportStore, question } = this.props
        const { showQuickFilter, filterByOpenEnded, quickFilters } = reportStore
        const filterKey = `${question.id}_${optionId}_${label}`
        const hasFilter = quickFilters.has(filterKey)
        const className = hasFilter
            ? 'fa-regular fa-square-check'
            : 'fa-regular fa-square'

        if (!showQuickFilter) return null

        return (
            <div className="quick-filter-check-container">
                <i
                    onClick={() => filterByOpenEnded(question, label, surveyDataIds, optionId)}
                    className={className}
                    style={{ marginRight: '5px' }}
                />
            </div>
        )
    }

    resultRows = (isPdfExport = false) => {
        const { isOpen, responses } = this.state
        const { question, reportStore, store } = this.props
        const { survey } = store
        return (
            <div style={{ marginTop: '10px' }}>
                {question.optionSet.options.map((option, i) => (
                    <Fragment key={option.id}>
                        <div
                            className=""
                            data-index={i}
                            style={{
                                display: 'flex',
                                fontSize: '1em',
                                justifyContent: 'space-between',
                                marginTop: '30px',
                                padding: '0px 25px',
                            }}
                        >
                            <div style={{ fontWeight: 700 }}>
                                {
                                    getOptionTitle({
                                        langs: option.langs,
                                        locale: reportStore.locale,
                                        defaultLocale: survey.metadata.defaultLocale,
                                        initialLocale: question.metadata.initialLocale,
                                    })
                                }
                                <span style={{ marginLeft: '5px' }}>
                                    (
                                    {i18n.t('report_total_responses')}
                                    :
                                    {' '}
                                    {R.pathOr({ numberOfResponses: 0 }, [option.id.toUpperCase()], responses).numberOfResponses}
                                    )
                                </span>
                            </div>
                            {!isPdfExport && (
                                <a
                                    data-id={option.id.toUpperCase()}
                                    onClick={this.toggleOpen}
                                    style={{
                                        cursor: 'pointer',
                                    }}
                                >
                                    {i18n.t('report_text_toggle')}
                                </a>
                            )}
                        </div>
                        <AnimateHeight
                            duration={250}
                            height={isPdfExport || isOpen[option.id.toUpperCase()] ? 'auto' : 0}
                            style={{
                                padding: '0px 25px',

                            }}
                        >
                            {this.renderResponseDetails(responses, option)}
                        </AnimateHeight>
                        {!isOpen[option.id.toUpperCase()] && (
                            <div style={{ paddingBottom: '5px', borderBottom: '1px solid #DDD' }} />
                        )}
                    </Fragment>
                ))}
            </div>
        )
    }

    renderResponseDetails = (responses, option) => (
        Object.hasOwnProperty.call(responses, option.id.toUpperCase()) && (
            responses[option.id.toUpperCase()].responses
                .map(({ label, numberOfResponses, surveyDataIds }, index) => (
                    <div
                        className="textResponse"
                        style={{ marginTop: '8px' }}
                        data-index={index}
                    >
                        <span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {this.renderFilter(label, option.id, surveyDataIds)}
                                {label}
                            </div>
                        </span>
                        <span>
                            (
                            {numberOfResponses}
                            )
                        </span>
                    </div>
                ))
        )
    )

    toggleOpen = (e) => {
        const { isOpen } = this.state
        const id = e.currentTarget.attributes.getNamedItem('data-id').value

        this.setState({
            isOpen: {
                ...isOpen,
                [id]: !isOpen[id],
            },
        })
    }

    render() {
        const { isLoading } = this.state
        const {
            pageId,
            store,
            reportStore,
            question,
        } = this.props
        const { survey } = store
        const questionTitle = getQuestionTitle({
            content: question.content.shortTitle,
            locale: reportStore.locale,
            defaultLocale: survey.metadata.defaultLocale,
            initialLocale: question.metadata.initialLocale,
        })
        const questionLongTitle = getQuestionTitle({
            content: question.content.longTitle,
            locale: reportStore.locale,
            defaultLocale: survey.metadata.defaultLocale,
            initialLocale: question.metadata.initialLocale,
        })
        return (
            <Panel cssClass="question textQuestion">
                <PanelHeader>
                    <div id={`questionTitle_${question.pageQuestionId}`} className="pageTitleContainer">
                        <ReportPageTitle
                            h2={questionTitle}
                            h3={questionLongTitle}
                            reportStore={reportStore}
                            questionTypeCode={question.questionTypeCode}
                        />
                        <QuestionEdit reportStore={reportStore} pageId={pageId} />
                    </div>
                </PanelHeader>
                <DebugInfo
                    store={store}
                    questionTypeCode={question.questionTypeCode}
                    reportStore={reportStore}
                    questionId={question.id}
                    pageQuestionId={question.pageQuestionId}
                />
                <div id={`questionData_${question.pageQuestionId}`}>
                    {isLoading && <div className="loader" />}
                    {(
                        question.hasHeaderGroups
                            ? this.renderGroupedRows()
                            : this.resultRows()
                    )}
                </div>
            </Panel>
        )
    }
}

export default TextListBox
